<template>
  <span>
    <confirm-modal
      id="previous-document-type-confirm"
      :v-if="confirmPreviousDocument"
      title="Are you sure you want to proceed?"
      :confirmation="prevDocTypeConfirmText"
      @submit="submit()"
    />
    <b-modal
      :id="this.$attrs.id"
      v-model="isVisible"
      title="Declaration information"
      size="lg"
    >
      <b-form>
        <validation-observer ref="formValidation">
          <b-form-group
            v-if="!clearance"
            label="Declaration type"
            label-for="arrived"
            label-class="mb-1"
          >
            <validation-provider
              #default="{ errors }"
              name="Declaration type"
              rules="required"
            >
              <b-form-radio-group
                id="arrived"
                v-model="hasArrived"
                :options="arrivedOptions"
                name="arrived"
                :state="errors.length > 0 ? false : null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <b-form-group
            label="Choose warehouse"
            label-for="vue-select"
          >
            <validation-provider
              #default="{ errors }"
              name="Warehouse"
              :rules="'required'"
            >
              <b-form-select
                id="warehouse"
                v-model="warehouse"
                :options="warehouses"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="shipment.entry_country === 'RO'"
            label="Previous document category"
            label-for="previous_document_category"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document category"
            >
              <b-form-select
                id="previous_document_category"
                v-model="previous_document_category"
                :options="previous_document_categories"
                placeholder="Choose document category"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="shipment.entry_country !== 'LT'"
            label="Previous document type"
            label-for="previous_document_type"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document type"
              :rules="shipment.entry_country !== 'RO' && declarationType === 'h1' ? 'required' : ''"
            >
              <b-form-select
                id="previous_document_type"
                v-model="previous_document_type"
                :options="previousDocumentTypeOptions"
                placeholder="Choose document type"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="shouldShowPreviousDocRef"
            label="Previous document reference"
            label-for="previous_document_reference"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document reference"
              :rules="shipment.entry_country !== 'RO' && declarationType === 'h1' ? 'required' : ''"
            >
              <b-form-input
                id="previous_document_reference"
                v-model="previous_document_reference"
                placeholder="Previous document reference"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="previousDocumentRequireNumberOfItemsMap[previous_document_type]"
            label="Previous document number of items"
            label-for="number_of_items"
          >
            <validation-provider
              #default="{ errors }"
              name="Previous document number of items"
              :rules="{ between: { min: 1, max: 99 }}"
            >
              <sup class="text-danger">{{ errors[0] }}</sup>
              <b-form-input
                id="previous_document_number_of_items"
                v-model.number="previous_document_number_of_items"
                placeholder="Previous document number of items"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="declarationType === 'h1'"
            label="Inland mode of transport"
            label-for="inland_mode_of_transport"
          >
            <validation-provider
              #default="{ errors }"
              name="Inland mode of transport"
              :rules="'required'"
            >
              <b-form-select
                id="inland_mode_of_transport"
                v-model="inland_mode_of_transport"
                :options="transport_types"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="declarationType === 'h1'"
            label="Mode of transport at the border"
            label-for="mode_of_transport_at_border"
          >
            <validation-provider
              #default="{ errors }"
              name="Mode of transport at the border"
              :rules="'required'"
            >
              <b-form-select
                id="mode_of_transport_at_border"
                v-model="mode_of_transport_at_border"
                :options="transport_types"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="mode_of_transport_at_border"
            label="Transport registration number"
            label-for="transport_registration_number"
          >
            <validation-provider
              #default="{ errors }"
              name="Transport registration number"
              :rules="'max:35'"
            >
              <b-form-input
                id="transport_registration_number"
                v-model="transport_registration_number"
                placeholder="Transport registration number"
                autocomplete="off"
                :state="errors.length > 0 ? false : null"
              />
            </validation-provider>
          </b-form-group>
          <b-form-group
            v-if="showNotArrivedOption && warehouse && !resendables"
            label="Some of selected containers/parcels are missing 'Arrived to facility' event"
            label-class="text-warning"
            label-for="send_arrived_to_facility"
          >
            <b-form-checkbox
              id="send_arrived_to_facility"
              v-model="send_arrived_to_facility"
              name="send_arrived_to_facility"
              value="true"
              unchecked-value="false"
            >
              Send "Arrived to facility" event
            </b-form-checkbox>
          </b-form-group>
          <b-form-group
            v-if="noaEnabled && !resendables"
            label="Notification of Arrival (NOA) Date/Time"
            label-for="noa_at"
          >
            <validation-provider
              #default="{ errors }"
              name="NOA date and time"
            >
              <b-input-group>
                <flat-pickr
                  id="noa_at"
                  ref="noaDatePicker"
                  v-model="noa_at"
                  :config="noaConfig"
                  class="form-control"
                  placeholder="Enter date and time YYYY-MM-DD HH:mm"
                  :state="errors.length > 0 ? false : null"
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    @click="$refs.noaDatePicker.fp.toggle()"
                  >
                    <feather-icon icon="CalendarIcon" />
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    @click="noa_at = null"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </validation-observer>
      </b-form>

      <template #modal-footer="">
        <b-button
          variant="primary"
          :disabled="!shipment.entry_country || (shipment.entry_country === 'NL' && !previous_document_type && !isNoPreviousDocumentSelected)"
          @click="validateSend()"
        >
          Submit declarations
        </b-button>
      </template>
    </b-modal>
  </span>
</template>

<script>

import {
  BButton,
  BForm,
  BFormGroup,
  BFormRadioGroup,
  BFormInput,
  BFormSelect,
  BFormCheckbox,
  BModal,
  BInputGroup,
  BInputGroupAppend,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import handleError from '@/views/components/errorHandler'
import ConfirmModal from '@core/components/confirmation/confirmModal.vue'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

export default {
  components: {
    ConfirmModal,
    ValidationProvider,
    ValidationObserver,
    BButton,
    BForm,
    BFormGroup,
    BFormRadioGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BModal,
    BInputGroup,
    BInputGroupAppend,
    flatPickr,
  },
  props: {
    clearance: {
      type: Boolean,
      default: false,
    },
    resendables: {
      type: Boolean,
      default: false,
    },
    shipment: {
      type: Object,
      required: true,
    },
    selectedParcels: {
      type: Array,
      required: true,
    },
    selectedContainers: {
      type: Array,
      required: true,
    },
    selectedAll: {
      type: Boolean,
      default: false,
    },
    declarationType: {
      type: String,
      default: 'h7',
    },
  },
  data() {
    return {
      arrivedOptions: [
        { text: 'A - Regular', value: true },
        { text: 'D - Pre-arrival', value: false },
      ],
      showNotArrivedOption: false,
      isVisible: false,
      confirmPreviousDocument: false,
      prevDocTypeConfirmText: '',
      hasArrived: null,
      warehouse: '',
      warehouses: [{ value: '', text: '----select----' }],
      previous_document_category: '',
      previous_document_type: null,
      previous_document_number_of_items: null,
      previous_document_categories: [
        { value: '', text: '----select----' },
        { value: 'X', text: 'Summary Declaration' },
        { value: 'Y', text: 'Initial Declaration' },
        { value: 'Z', text: 'Previous Document' },
      ],
      previous_document_reference: '',
      inland_mode_of_transport: this.shipment?.company?.config?.defaultTransportMode ?? '',
      mode_of_transport_at_border: this.shipment?.company?.config?.defaultTransportMode ?? '',
      transport_registration_number: this.shipment?.company?.config?.defaultTransportRegistrationNumber ?? '',
      transport_types: this.$classifiers().getOptions('transport_types'),
      send_arrived_to_facility: this.shipment.company.config?.arrivedToFacilityEventSelected ?? false,
      noa_at: null,
    }
  },
  computed: {
    noaEnabled() {
      return this.shipment?.company?.config?.enableNOA === true
    },
    noaConfig() {
      return {
        enableTime: true,
        time_24hr: true,
        altInput: true,
        altFormat: 'Y-m-d H:i',
        dateFormat: 'Y-m-d H:i',
        maxDate: new Date(),
        allowInput: true,
        clickOpens: false,
      }
    },
    shouldShowPreviousDocRef() {
      if (this.shipment.entry_country === 'LT') {
        return false
      }
      if (this.shipment.entry_country === 'NL' && this.isNoPreviousDocumentSelected) {
        return false
      }
      return true
    },
    isNoPreviousDocumentSelected() {
      if (this.shipment.entry_country !== 'NL') {
        return false
      }
      const selected = this.previousDocumentTypes.find(type => type.type === this.previous_document_type)
      return selected?.name === 'No previous Document'
    },
    previousDocumentTypes() {
      return this.$store.getters['app/previousDocumentTypes'](this.shipment.entry_country) || []
    },
    previousDocumentTypeOptions() {
      const types = this.previousDocumentTypes
      return [{ value: null, text: '----select----', confirm: false, require_number_of_items: false }]
        .concat(types.map(type => ({
          text: type.name,
          value: type.type,
          confirm: type.confirm,
          require_number_of_items: type.require_number_of_items,
        })))
    },
    previousDocumentRequireNumberOfItemsMap() {
      return this.previousDocumentTypes.reduce((map, type) => {
        map[type.type] = type.require_number_of_items
        return map
      }, {})
    },
  },
  watch: {
    selectedParcels() {
      this.handleSendH7Declarations()
    },
    selectedContainers() {
      this.handleSendH7Declarations()
    },
    selectedAll() {
      this.handleSendH7Declarations()
    },
    previous_document_type(newVal) {
      if (this.shipment.entry_country === 'NL') {
        const selectedType = this.previousDocumentTypes.find(x => x.type === newVal)
        if (selectedType && selectedType.text === 'No previous Document') {
          this.previous_document_reference = null
        }
      }
      if (this.previousDocumentRequireNumberOfItemsMap[newVal] === false) {
        this.previous_document_number_of_items = null
      }
    },
  },
  created() {
    this.$store.dispatch('app/getPreviousDocumentTypes', {
      entryCountry: this.shipment.entry_country,
      toast: this.$toast,
    })
    this.$http.get(`/v1/companies/${this.shipment.company_id}/warehouses`)
      .then(response => {
        const warehouses = response.data.data.filter(x => x.country === this.shipment.entry_country)
        const moreThan1Company = warehouses.length > 0
          ? (new Set(warehouses.map(warehouse => warehouse?.company?.id))).size > 1
          : false

        this.warehouses = this.warehouses.concat(warehouses.map(warehouse => ({
          value: warehouse.id,
          text: moreThan1Company ? `${warehouse.name} (${warehouse.company.name})` : warehouse.name,
        })))
      })
    this.handleSendH7Declarations()
  },
  methods: {
    handleSendH7Declarations() {
      this.showNotArrivedOption = false
      this.$http.post(
        `/v1/companies/${this.shipment.company_id}/shipments/${this.shipment.id}/parcels-arrived`,
        this.getSelected(),
      )
        .then(response => {
          this.showNotArrivedOption = !!response.data.hasMissingEvent
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
    },
    getSelected() {
      const ret = {
        all: null,
        containers: null,
        parcels: null,
      }
      if (this.selectedAll) {
        // send only all if it is selected
        ret.all = true
      } else {
        // send selected containers and separately selected parcels
        ret.containers = this.selectedContainers
        ret.parcels = this.selectedParcels
        if (ret.containers.length) {
          // remove parcels where container is selected
          const parcelIds = this.getSelectedContainersParcels()
          ret.parcels = ret.parcels.filter(x => !parcelIds.includes(x))
        }
      }
      return ret
    },
    getSelectedContainersParcels() {
      if (!this.selectedParcels || !this.selectedParcels.length || !Array.isArray(this.allParcelsData)) {
        return []
      }
      const containerCodesSet = new Set(this.selectedContainers)
      return (this.allParcelsData || [])
        .map(parcelGroup => Object.values(parcelGroup || {}).flat()
          .filter(parcel => parcel && containerCodesSet.has(parcel.container_code))
          .map(parcel => parcel.id))
        .flat()
    },
    validateSend() {
      this.$refs.formValidation.validate().then(success => {
        if (!success) {
          return
        }
        const selectedDocumentType = this.previousDocumentTypes.find(type => type.type === this.previous_document_type)
        if (selectedDocumentType?.confirm) {
          this.confirmPreviousDocument = true
          this.$bvModal.show('previous-document-type-confirm')
          this.prevDocTypeConfirmText = `You selected "${selectedDocumentType.name}", are you sure you want to continue`
        } else {
          this.submit()
        }
      })
    },
    submit() {
      this.$bvModal.hide('previous-document-type-confirm')
      this.$emit('submit', {
        previous_document_type: this.previous_document_type,
        previous_document_category: this.previous_document_category,
        previous_document_reference: this.previous_document_reference,
        send_arrived_to_facility: this.send_arrived_to_facility,
        warehouse_id: this.warehouse,
        has_arrived: this.hasArrived,
        inland_mode_of_transport: this.inland_mode_of_transport,
        mode_of_transport_at_border: this.mode_of_transport_at_border,
        transport_registration_number: this.transport_registration_number,
        previous_document_number_of_items: this.previous_document_number_of_items,
        noa_at: this.noa_at,
      })
      this.previous_document_type = null
      this.previous_document_category = ''
      this.previous_document_reference = null
      this.warehouse = ''
      this.isVisible = false
      this.previous_document_number_of_items = null
      this.noa_at = null
    },
  },
}
</script>

<style scoped>

</style>
